import { toGenericPriceKey, toOracleKey } from '../../assets'
import { GenericTokenList } from '../../types'
import { apyToAprPercent, parseRawAmount } from '../../utils/parsing'
import { composeVaultDisplayName } from '../displayName'
import { curatorNameFromVaultName } from '../curatorName'
import { deriveConvertToAssets } from '../sharePrice'
import { MorphoVault, MorphoVaults, VaultCuratorMeta } from './types'

/** MetaMorpho shares are always 18-decimal (via the v1.1 decimals offset). */
const MORPHO_SHARE_DECIMALS = 18

const MORPHO_API_URL = 'https://blue-api.morpho.org/graphql'
const PAGE_SIZE = 200

/**
 * Spam/dust floor replacing the old `listed: true` filter.
 *
 * The query filtered on `listed` from the start, and until Aug-2026 that flag
 * covered the whole curated catalogue. Then Morpho re-scoped it to "listed in
 * the Morpho app" and it collapsed — 48 of 450 chain-1 vaults, 24 on Base,
 * ZERO on Worldchain — which silently dropped two thirds of our Morpho vault
 * rows (311 → 87 on chain 1). Vault visibility is now OUR call, not the
 * flag's: take everything above a token dust floor. Spam vaults price to ~$0
 * under Morpho's oracles, so the floor is what actually screens them.
 */
const MIN_TOTAL_ASSETS_USD = 100

/**
 * MetaMorpho vaults query against the public Morpho API.
 *
 * `netApy` is the depositor-visible APY *after* performance fee, so we
 * don't need to apply the fee ourselves. `rewards.supplyApr` is already
 * APR and summed into `rewardsRate` downstream.
 */
const vaultsQuery = (first: number, skip: number, chainId: string) => `
query GetVaults {
  vaults(
    first: ${first},
    skip: ${skip},
    where: { chainId_in: [${chainId}], totalAssetsUsd_gte: ${MIN_TOTAL_ASSETS_USD} },
    orderBy: TotalAssetsUsd,
    orderDirection: Desc
  ) {
    items {
      address
      symbol
      name
      whitelisted: listed
      asset {
        address
        name
        symbol
        decimals
        priceUsd
      }
      liquidity {
        underlying
        usd
      }
      state {
        totalAssets
        totalAssetsUsd
        totalSupply
        apy
        netApy
        fee
        timelock
        owner
        curator
        guardian
        curators {
          id
          name
          image
          verified
        }
        rewards {
          supplyApr
          asset {
            address
          }
        }
      }
    }
  }
}
`

interface ApiVaultItem {
  address: string
  symbol: string | null
  name: string | null
  whitelisted: boolean | null
  asset: {
    address: string
    name: string | null
    symbol: string | null
    decimals: number
    priceUsd: string | number | null
  } | null
  liquidity: {
    underlying: string | null
    usd: string | number | null
  } | null
  state: {
    totalAssets: string | null
    totalAssetsUsd: string | number | null
    totalSupply: string | null
    apy: string | number | null
    netApy: string | number | null
    fee: string | number | null
    timelock: string | number | null
    owner: string | null
    curator: string | null
    guardian: string | null
    curators: Array<{
      id: string | null
      name: string | null
      image: string | null
      verified: boolean | null
    }> | null
    rewards: Array<{
      supplyApr: string | number | null
      asset: { address: string } | null
    }> | null
  } | null
}

async function fetchPage(
  chainId: string,
  first: number,
  skip: number,
): Promise<ApiVaultItem[]> {
  const response = await fetch(MORPHO_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query: vaultsQuery(first, skip, chainId) }),
  })
  if (!response.ok) {
    throw new Error(
      `Morpho vaults API failed: ${response.status} - ${response.statusText}`,
    )
  }
  const json: any = await response.json()
  if (json?.errors?.length) {
    throw new Error(
      `Morpho vaults API GraphQL error: ${json.errors
        .map((e: any) => e?.message)
        .join('; ')}`,
    )
  }
  return (json?.data?.vaults?.items ?? []) as ApiVaultItem[]
}

type ApiVaultRewards = NonNullable<
  NonNullable<ApiVaultItem['state']>['rewards']
>
type ApiVaultCurators = NonNullable<
  NonNullable<ApiVaultItem['state']>['curators']
>

const parseCurators = (
  curators: ApiVaultCurators | null | undefined,
): VaultCuratorMeta[] | undefined => {
  if (!curators?.length) return undefined
  const out: VaultCuratorMeta[] = []
  for (const c of curators) {
    const name = c?.name?.trim()
    if (!name) continue
    out.push({
      id: c.id ?? '',
      name,
      image: c.image ?? undefined,
      verified: c.verified ?? undefined,
    })
  }
  return out.length > 0 ? out : undefined
}

const sumRewardsApr = (rewards: ApiVaultRewards | null | undefined): number => {
  if (!rewards) return 0
  let total = 0
  for (const r of rewards) {
    const apr = Number(r?.supplyApr ?? 0)
    if (Number.isFinite(apr)) total += apr
  }
  return total * 100
}

function parseVault(
  v: ApiVaultItem,
  chainId: string,
  prices: { [asset: string]: number },
  tokenList: GenericTokenList,
): MorphoVault | null {
  const address = (v?.address ?? '').toLowerCase()
  const assetAddr = (v?.asset?.address ?? '').toLowerCase()
  if (!address || !assetAddr) return null

  const state = v.state ?? ({} as NonNullable<ApiVaultItem['state']>)
  const assetMeta = tokenList[assetAddr]
  const decimals = Number(v.asset?.decimals ?? assetMeta?.decimals ?? 18)

  const totalAssetsRaw = state.totalAssets?.toString() ?? '0'
  const totalSupplyRaw = state.totalSupply?.toString() ?? '0'
  const totalAssetsFormatted = Number(parseRawAmount(totalAssetsRaw, decimals))
  const convertToAssets = deriveConvertToAssets(
    totalAssetsRaw,
    totalSupplyRaw,
    MORPHO_SHARE_DECIMALS,
  )

  const liquidityRaw = v.liquidity?.underlying?.toString() ?? '0'
  const liquidityFormatted = Number(parseRawAmount(liquidityRaw, decimals))
  const liquidityUsd = Number(v.liquidity?.usd ?? 0)

  const oracleKey =
    toOracleKey(assetMeta?.assetGroup) ?? toGenericPriceKey(assetAddr, chainId)
  const apiPriceUsd = Number(v.asset?.priceUsd ?? 0)
  const resolvedPriceUsd =
    prices[oracleKey] ?? prices[assetAddr] ?? apiPriceUsd ?? 0

  // `netApy` is net-of-fee — what a depositor actually sees from the
  // base Morpho Blue allocations. Fall back to `apy` if netApy is null.
  const netApy = Number(state.netApy ?? state.apy ?? 0)
  const supplyRate = apyToAprPercent(netApy * 100)
  const rewardsRate = sumRewardsApr(state.rewards)

  // `totalAssetsUsd` from the API is authoritative when present — it
  // uses Morpho's own oracle which may diverge from our price map.
  const apiTotalAssetsUsd = Number(state.totalAssetsUsd ?? 0)
  const totalAssetsUsd =
    apiTotalAssetsUsd || totalAssetsFormatted * resolvedPriceUsd

  const curators = parseCurators(state.curators)
  const primaryCurator = curators?.[0]
  // The API publishes curator metadata only for app-listed vaults; the wider
  // catalogue (see MIN_TOTAL_ASSETS_USD) mostly comes without it, so fall
  // back to the vault's own name — NEVER over an API-supplied curator.
  const curatorName =
    primaryCurator?.name ??
    curatorNameFromVaultName(v.name, v.asset?.symbol ?? assetMeta?.symbol)

  return {
    address,
    underlying: assetAddr,
    symbol: v.symbol ?? '',
    name: v.name ?? '',
    displayName: composeVaultDisplayName(
      'Morpho',
      curatorName,
      assetMeta,
      v.name ?? undefined,
    ),
    decimals,
    totalAssets: totalAssetsRaw,
    totalSupply: totalSupplyRaw,
    convertToAssets,
    supplyRate,
    rewardsRate,
    depositRate: supplyRate + rewardsRate,
    fee: Number(state.fee ?? 0) * 100,
    timelock: Number(state.timelock ?? 0),
    // Since Aug-2026 `listed` means "listed in the Morpho app" (a far smaller
    // set than the old curated flag) — informational only, never a row filter.
    whitelisted: v.whitelisted ?? true,
    owner: state.owner?.toLowerCase() || undefined,
    curator: state.curator?.toLowerCase() || undefined,
    guardian: state.guardian?.toLowerCase() || undefined,
    curatorName,
    curatorImage: primaryCurator?.image,
    curators,
    asset: assetMeta,
    priceUsd: resolvedPriceUsd || undefined,
    totalAssetsFormatted,
    totalAssetsUsd,
    liquidity: liquidityRaw,
    liquidityFormatted,
    liquidityUsd: liquidityUsd || liquidityFormatted * resolvedPriceUsd,
  }
}

/**
 * Fetches MetaMorpho vaults from the public Morpho API for a single chain.
 *
 * Covers all chains indexed by `blue-api.morpho.org` (Ethereum mainnet,
 * Base, Arbitrum, Polygon, Unichain, etc.). Pages through at most
 * `maxItems` entries — vault counts per chain are well under 200 in
 * practice but we loop defensively for mainnet where count is highest.
 */
export async function fetchMorphoVaultsFromApi(
  chainId: string,
  prices: { [asset: string]: number } = {},
  tokenList: GenericTokenList = {},
  maxItems = 1000,
): Promise<MorphoVaults> {
  const out: MorphoVaults = {}

  let skip = 0
  while (skip < maxItems) {
    const page = await fetchPage(chainId, PAGE_SIZE, skip)
    if (page.length === 0) break

    for (const v of page) {
      const parsed = parseVault(v, chainId, prices, tokenList)
      if (parsed) out[parsed.address] = parsed
    }

    if (page.length < PAGE_SIZE) break
    skip += PAGE_SIZE
  }

  return out
}
